import React, { useState } from 'react';
import { BiCheck, BiCopy, BiError, BiInfoCircle, BiTime, BiX } from 'react-icons/bi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { type ExtendedThemeConfig, useTheme } from '../../../theme';
import type { MarkdownBlock as MarkdownBlockType } from '../../../types/blocks';
import TTSButton from '../../TTSButton';
import { stripMarkdown } from '../../../helpers/textExtraction';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../state/store';

interface MarkdownBlockProps {
  block: MarkdownBlockType;
  blockId: string;
}

const MarkdownBlock: React.FC<MarkdownBlockProps> = ({ block, blockId }) => {
  const theme = useTheme();
  const [isCopied, setIsCopied] = useState(false);
  const { demo_account: isDemoAccount } = useSelector(
    (state: RootState) => state.company.company_details
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(block.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy markdown content:', error);
    }
  };

  const getModeConfig = (mode?: string) => {
    switch (mode) {
      case 'handle_error':
        return {
          icon: <BiError />,
          title: 'Error',
          showCopyButton: false,
          variant: 'error' as const
        };
      case 'handle_token_limit_error':
        return {
          icon: <BiTime />,
          title: 'Token Limit Exceeded',
          showCopyButton: false,
          variant: 'warning' as const
        };
      case 'handle_clarification':
        return {
          icon: <BiInfoCircle />,
          title: 'Clarification Needed',
          showCopyButton: false,
          variant: 'info' as const
        };
      case 'handle_out_of_scope':
        return {
          icon: <BiX />,
          title: 'Out of Scope',
          showCopyButton: false,
          variant: 'warning' as const
        };
      default:
        return {
          icon: null,
          title: null,
          showCopyButton: true,
          variant: 'default' as const
        };
    }
  };

  const modeConfig = getModeConfig(block.mode);

  return (
    <MarkdownWrapper $theme={theme} $variant={modeConfig.variant}>
      {modeConfig.title && (
        <ModeHeader $theme={theme} $variant={modeConfig.variant}>
          <ModeIcon $variant={modeConfig.variant} $theme={theme}>
            {modeConfig.icon}
          </ModeIcon>
          <ModeTitle $theme={theme}>{modeConfig.title}</ModeTitle>
        </ModeHeader>
      )}
      <ContentWrapper $hasHeader={!!modeConfig.title} $theme={theme} $variant={modeConfig.variant}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.content}</ReactMarkdown>
      </ContentWrapper>
      {modeConfig.showCopyButton && (
        <ActionsContainer>
          {isDemoAccount && <TTSButton blockId={blockId} text={stripMarkdown(block.content)} />}
          <CopyButton onClick={handleCopy} isActive={isCopied} $theme={theme}>
            {isCopied ? <BiCheck /> : <BiCopy />}
          </CopyButton>
        </ActionsContainer>
      )}
    </MarkdownWrapper>
  );
};

export default MarkdownBlock;

type VariantType = 'default' | 'error' | 'warning' | 'info';

const getVariantColors = (variant: VariantType, theme: ExtendedThemeConfig) => {
  const isDark = theme.mode === 'dark';

  switch (variant) {
    case 'error':
      return {
        border: '#ef4444',
        background: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
        icon: '#ef4444',
        header: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)'
      };
    case 'warning':
      return {
        border: '#f59e0b',
        background: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
        icon: '#f59e0b',
        header: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)'
      };
    case 'info':
      return {
        border: '#3b82f6',
        background: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
        icon: '#3b82f6',
        header: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)'
      };
    default:
      return {
        border: 'transparent',
        background: theme.colors.assistantBubbleBg,
        icon: theme.colors.assistantBubbleText,
        header: 'transparent'
      };
  }
};

const MarkdownWrapper = styled.div<{
  $theme: ExtendedThemeConfig;
  $variant: VariantType;
}>`
  margin-top: 12px;
  width: 100%;
  position: relative;
  border: 1px solid ${(props) => getVariantColors(props.$variant, props.$theme).border};
  border-radius: 8px;
  background-color: ${(props) => getVariantColors(props.$variant, props.$theme).background};
  overflow: hidden;

  ${(props) =>
    props.$variant === 'default' &&
    `
    border: none;
    background-color: transparent;
  `}
`;

const ModeHeader = styled.div<{
  $theme: ExtendedThemeConfig;
  $variant: VariantType;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: ${(props) => getVariantColors(props.$variant, props.$theme).header};
  border-bottom: 1px solid ${(props) => getVariantColors(props.$variant, props.$theme).border};
  font-weight: 600;
  font-size: 14px;
`;

const ModeIcon = styled.div<{
  $variant: VariantType;
  $theme: ExtendedThemeConfig;
}>`
  display: flex;
  align-items: center;
  color: ${(props) => getVariantColors(props.$variant, props.$theme).icon};
  font-size: 16px;
`;

const ModeTitle = styled.span<{ $theme: ExtendedThemeConfig }>`
  color: ${(props) => props.$theme.colors.mainText};
`;

const ContentWrapper = styled.div<{
  $hasHeader: boolean;
  $theme: ExtendedThemeConfig;
  $variant?: VariantType;
}>`
  position: relative;
  left: 5px;
  width: 100%;
  max-width: none;
  padding: ${(props) => (props.$hasHeader ? '16px' : '0')};
  color: ${(props) =>
    props.$variant === 'default'
      ? props.$theme.colors.assistantBubbleText
      : props.$theme.colors.mainText};
  box-sizing: border-box;

  /* Markdown content styling */
  p {
    width: 100%;
    margin: 0 0 12px 0;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    width: 100%;
    margin: 0 0 12px 0;
    line-height: 1.3;
    color: ${(props) =>
      props.$variant === 'default'
        ? props.$theme.colors.assistantBubbleText
        : props.$theme.colors.mainText};

    &:first-child {
      margin-top: 0;
    }
  }

  ul,
  ol {
    width: 100%;
    margin: 0 0 12px 0;
    padding-left: 20px;
    box-sizing: border-box;
  }

  li {
    margin-bottom: 4px;
  }

  code {
    background-color: ${(props) =>
      props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(128, 128, 128, 0.1)'};
    color: ${(props) =>
      props.$variant === 'default'
        ? props.$theme.colors.assistantBubbleText
        : props.$theme.colors.mainText};
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
  }

  pre {
    background-color: ${(props) =>
      props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(128, 128, 128, 0.1)'};
    border: 1px solid
      ${(props) =>
        props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(128, 128, 128, 0.2)'};
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0 0 12px 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    code {
      background: none;
      padding: 0;
      color: ${(props) =>
        props.$variant === 'default'
          ? props.$theme.colors.assistantBubbleText
          : props.$theme.colors.mainText};
    }
  }

  blockquote {
    width: 100%;
    border-left: 4px solid ${(props) => props.$theme.colors.primary};
    padding-left: 16px;
    margin: 0 0 12px 0;
    font-style: italic;
    opacity: 0.8;
    color: ${(props) =>
      props.$variant === 'default'
        ? props.$theme.colors.assistantBubbleText
        : props.$theme.colors.mainText};
    box-sizing: border-box;
  }

  /* Links */
  a {
    color: ${(props) => props.$theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Tables */
  .table-container {
    width: 100%;
    overflow-x: auto;
    margin: 0 0 12px 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    table-layout: auto;
  }

  th,
  td {
    border: 1px solid
      ${(props) =>
        props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(128, 128, 128, 0.2)'};
    padding: 8px 12px;
    text-align: left;
  }

  th {
    background-color: ${(props) =>
      props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(128, 128, 128, 0.05)'};
    font-weight: 600;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;

const CopyButton = styled.button<{
  $theme: ExtendedThemeConfig;
  isActive: boolean;
}>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${(props) =>
    props.isActive ? props.$theme.colors.primary : props.$theme.colors.sidebarGroupColor};
  opacity: ${(props) => (props.isActive ? 1 : 0.6)};
  font-size: 16px;

  &:hover {
    opacity: 1;
    background-color: ${(props) => props.$theme.colors.messageBoxBorder};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    font-size: 16px;
  }
`;
